import { useEffect, useState, useRef } from 'react';
import { Theme, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { BasicSelectWithDescriptions } from 'sharedComponents/Select/BasicSelectWithDescriptions';
import preferencesAPI from 'api/preferencesAPI';
import { SuccessSnackbar } from 'sharedComponents/Snackbar/SuccessSnackbar';
import { JobSeekingStatusDropdownProps } from './types';
import { StatusOption } from './types';
import { ConnectJobSeekingStatusDropdownDataTestIds } from 'data-testids/ConnectDataTestIds';
import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';

export const JobSeekingStatusDropdown: React.FC<JobSeekingStatusDropdownProps> = ({
  dataTestId = ConnectJobSeekingStatusDropdownDataTestIds.DROPDOWN,
  userId,
  userJobSeekingStatus = '',
  onChange,
  autoSave = false,
  width = '100%',
}) => {
  const theme = useTheme();
  const { connectContextActions } = useConnectContext();
  const [selectedStatus, setSelectedStatus] = useState<string>(userJobSeekingStatus || '');
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowSuccessToastMessage, setShouldShowSuccessToastMessage] = useState(false);
  const isInitialMount = useRef(true);

  const statusOptions: StatusOption[] = [
    {
      value: 'open_to_work_and_matches',
      label: 'Open to matches',
      description:
        'Actively looking for a new role and open to being matched & contacted by principals directly.',
      icon: <CheckCircleOutlineIcon />,
      iconColor: theme.palette.primary.main,
    },
    {
      value: 'open_to_work',
      label: 'Open to work',
      description:
        "You're not open to being matched with principals, but are open to job opportunities.",
      icon: <SearchIcon />,
      iconColor: theme.palette.warning.main,
    },
    {
      value: 'closed_to_work',
      label: 'Closed to work',
      description: "You're not looking for a new job at this time.",
      icon: <CloseIcon />,
      iconColor: theme.palette.error.main,
    },
  ];

  useEffect(() => {
    if (!autoSave || isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    async function updateJobSeekingStatus() {
      if (!userId || !selectedStatus) return;

      try {
        setIsLoading(true);
        const updatedPreference = {
          job_seeking_status: selectedStatus,
        };
        const response = await preferencesAPI.update(userId, updatedPreference);
        connectContextActions.setJobSeekingStatus({
          status: response.job_seeking_status,
          updated_at: response.job_seeking_status_updated_at,
        });
        setShouldShowSuccessToastMessage(true);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }

    if (selectedStatus && selectedStatus !== userJobSeekingStatus) {
      updateJobSeekingStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, userId, autoSave]);

  useEffect(() => {
    setSelectedStatus(userJobSeekingStatus || '');
  }, [userJobSeekingStatus]);

  return (
    <>
      <BasicSelectWithDescriptions
        options={statusOptions}
        selectedOption={selectedStatus}
        onChange={onChange || ((newStatus: string) => setSelectedStatus(newStatus))}
        dataTestId={dataTestId}
        size="medium"
        isLoading={isLoading}
        sx={getStyles(theme, width).dropdown}
        menuSx={getStyles(theme, width).menu}
      />
      {autoSave && (
        <SuccessSnackbar
          message="Successfully saved"
          onClose={() => setShouldShowSuccessToastMessage(false)}
          open={shouldShowSuccessToastMessage}
          dataTestId={ConnectJobSeekingStatusDropdownDataTestIds.SUCCESS_TOAST}
          position={{ vertical: 'bottom', horizontal: 'left' }}
        />
      )}
    </>
  );
};

const getStyles = (theme: Theme, width?: string | { [key: string]: string }) => ({
  dropdown: {
    width: width,
    minWidth: '190px',
    height: '42px',
    '& .MuiSelect-select': {
      minHeight: 'auto',
      padding: '11px 16px',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.borderRadius * 0.5,
    },
  },
  menu: {
    width: 'auto',
    maxWidth: width,
  },
});
